<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useLeadsStore } from 'dashboard/stores/leads';
import LeadsInbox from './LeadsInbox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LeadImportDialog from 'dashboard/components-next/Crm/LeadImportDialog.vue';
import NewLeadDialog from 'dashboard/components-next/Crm/NewLeadDialog.vue';

const { t } = useI18n();
const route = useRoute();
const leadsStore = useLeadsStore();

const TABS = [
  { key: 'inbox',  labelKey: 'LEADS.TABS.INBOX' },
  { key: 'all',    labelKey: 'LEADS.TABS.ALL' },
  { key: 'tasks',  labelKey: 'LEADS.TABS.TASKS' },
];

const activeTab = ref('inbox');

// Captação de leads: importação CSV (movida do Kanban para cá - esta é a
// interface central de Leads). Fontes (site/API/poller) ficam em
// Configurações → Fontes de leads, linkadas no header.
const importDialogRef = ref(null);
const newLeadDialogRef = ref(null);
const leadSourcesRoute = {
  name: 'settings_lead_sources',
  params: { accountId: route.params.accountId },
};

function openImport() {
  importDialogRef.value?.dialogRef?.open();
}

function openNewLead() {
  newLeadDialogRef.value?.open();
}

function onLeadCreated() {
  // Recarrega a inbox de triagem (caso o lead novo apareça lá).
  activeTab.value = 'inbox';
}

async function onImport(file) {
  try {
    await leadsStore.import(file);
    importDialogRef.value?.dialogRef?.close();
    useAlert(t('PIPELINES.LEAD.IMPORT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error.response?.data?.message || t('PIPELINES.LEAD.IMPORT.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-[88rem] mx-auto px-6 py-6 flex flex-col gap-4">
      <div>
        <div class="flex flex-col items-start w-full">
          <div class="flex items-center justify-between w-full gap-4 min-h-8 mb-1">
            <h1 class="text-heading-1 text-n-slate-12">{{ t('LEADS.HEADER.TITLE') }}</h1>
            <div class="flex items-center gap-2 shrink-0">
              <router-link :to="leadSourcesRoute">
                <NextButton
                  faded
                  slate
                  size="sm"
                  icon="i-lucide-plug"
                  :label="t('LEADS.HEADER.SOURCES')"
                />
              </router-link>
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-upload"
                :label="t('LEADS.HEADER.IMPORT_CSV')"
                @click="openImport"
              />
              <NextButton
                size="sm"
                icon="i-lucide-plus"
                :label="t('LEADS.HEADER.CREATE')"
                @click="openNewLead"
              />
            </div>
          </div>
          <p class="text-body-main text-n-slate-11 max-w-3xl mb-0">
            {{ t('LEADS.HEADER.SUBTITLE') }}
          </p>
        </div>
      </div>

      <div class="border-b border-n-weak">
        <nav class="flex gap-6" role="tablist">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            role="tab"
            type="button"
            class="relative pb-3 text-body-main font-medium transition-colors border-b-2 -mb-px"
            :class="activeTab === tab.key
              ? 'text-n-blue-11 border-n-blue-11'
              : 'text-n-slate-11 border-transparent hover:text-n-slate-12'"
            @click="activeTab = tab.key"
          >
            {{ t(tab.labelKey) }}
          </button>
        </nav>
      </div>

      <LeadsInbox v-if="activeTab === 'inbox'" />
      <div v-else-if="activeTab === 'all'" class="flex flex-col items-center justify-center py-24 text-n-slate-11">
        <i class="i-lucide-users size-10 mb-4 opacity-40" />
        <p class="text-body-main">{{ t('LEADS.ALL.PLACEHOLDER') }}</p>
      </div>
      <div v-else-if="activeTab === 'tasks'" class="flex flex-col items-center justify-center py-24 text-n-slate-11">
        <i class="i-lucide-clipboard-list size-10 mb-4 opacity-40" />
        <p class="text-body-main">{{ t('LEADS.TASKS.PLACEHOLDER') }}</p>
      </div>
    </div>

    <LeadImportDialog
      ref="importDialogRef"
      :is-importing="leadsStore.isImporting"
      @import="onImport"
    />
    <NewLeadDialog ref="newLeadDialogRef" @created="onLeadCreated" />
  </div>
</template>